/**
 * @module NotificationSchema
 * @package @geenius/notifications-convex
 * @description Defines the mergeable Convex table contract for notification
 * inbox entries and persisted delivery preferences. Consumers can spread these
 * tables into a larger application schema while the package exports a default
 * schema for its own packaged queries and mutations.
 */

import { defineSchema, defineTable } from 'convex/server'
import { v } from 'convex/values'

const notificationChannelValidator = v.union(
  v.literal('in-app'),
  v.literal('email'),
  v.literal('push'),
  v.literal('sms'),
)

const notificationStatusValidator = v.union(
  v.literal('unread'),
  v.literal('read'),
  v.literal('dismissed'),
)

const notificationTypeValidator = v.union(
  v.literal('info'),
  v.literal('success'),
  v.literal('warning'),
  v.literal('error'),
  v.literal('mention'),
  v.literal('invite'),
  v.literal('payment'),
  v.literal('system'),
)

const notificationMetadataValidator = v.record(
  v.string(),
  v.union(v.string(), v.number(), v.boolean(), v.null()),
)

const notificationChannelsPreferencesValidator = v.object({
  'in-app': v.boolean(),
  email: v.boolean(),
  push: v.boolean(),
  sms: v.boolean(),
})

const notificationTypesPreferencesValidator = v.object({
  info: v.boolean(),
  success: v.boolean(),
  warning: v.boolean(),
  error: v.boolean(),
  mention: v.boolean(),
  invite: v.boolean(),
  payment: v.boolean(),
  system: v.boolean(),
})

/**
 * Mergeable Convex tables exposed for consumer schema composition.
 */
export const notificationsTables = {
  notifications: defineTable({
    type: notificationTypeValidator,
    title: v.string(),
    body: v.string(),
    channel: notificationChannelValidator,
    status: notificationStatusValidator,
    userId: v.string(),
    actorId: v.optional(v.string()),
    actorName: v.optional(v.string()),
    actorAvatar: v.optional(v.string()),
    resourceType: v.optional(v.string()),
    resourceId: v.optional(v.string()),
    resourceUrl: v.optional(v.string()),
    actionLabel: v.optional(v.string()),
    actionUrl: v.optional(v.string()),
    metadata: v.optional(notificationMetadataValidator),
    createdAt: v.string(),
    readAt: v.optional(v.string()),
  })
    .index('by_userId_status', ['userId', 'status'])
    .index('by_userId_createdAt', ['userId', 'createdAt'])
    .index('by_type', ['type'])
    .index('by_resourceId', ['resourceId']),

  notification_preferences: defineTable({
    userId: v.string(),
    channels: notificationChannelsPreferencesValidator,
    types: notificationTypesPreferencesValidator,
    quietHours: v.optional(v.object({ start: v.string(), end: v.string() })),
    timezone: v.optional(v.string()),
  })
    .index('by_userId', ['userId']),
}

/**
 * Default Convex schema export used by the packaged notifications backend.
 */
const schema = defineSchema(notificationsTables)

export default schema
